import { KRAKEN_OCTOPUS } from "@/components/PublicLayout";
import { ShieldCheck, BadgeCheck, HeartPulse, Target, Award } from "lucide-react";

const HEAD_COACH = {
  name: "Jackson Goodman",
  title: "Founder · Head Coach",
  badges: ["Army Veteran", "Faith-Driven", "Leadership Development", "Skill Development", "Mentor"],
  bio: [
    "Coach Jackson Goodman is the Founder and Head Coach of Coastal Kraken Baseball. He brings a strong background in athletics, coaching, military service, ministry, and leadership development.",
    "Coach Jackson has 4 years of professional coaching experience along with an additional 4 years of volunteer coaching experience. His coaching approach is built on relationships, trust, discipline, accountability, and encouragement. He believes every athlete develops differently and deserves to be coached in a way that challenges them, builds their confidence, and helps them grow both on and off the field.",
    "As a United States Army veteran and former competitive athlete, Coach Jackson understands the value of preparation, teamwork, toughness, humility, and servant leadership. His goal is to develop players who compete with effort, respect the game, support their teammates, and respond to challenges with character.",
    "Coach Jackson's mission is bigger than baseball. He is committed to helping Coastal Kraken athletes become better players, better teammates, better leaders, and better young men.",
  ],
  philosophy: [
    "Respect through actions",
    "Integrity in all situations",
    "Discipline in preparation and effort",
    "Accountability to teammates, coaches, and self",
    "Servant leadership",
    "Character development alongside athletic growth",
  ],
};

const CERTIFICATIONS = [
  { label: "USA Baseball Coach A Certified", icon: Award },
  { label: "USA Baseball Coach B Certified", icon: Award },
  { label: "USA Baseball Coach C Certified", icon: Award },
  { label: "Pitch Smart Certified", icon: Target },
  { label: "USA Baseball First Aid Certified", icon: HeartPulse },
];

const COACHES = [
  {
    name: "Anthony Sampogna",
    title: "Coach",
    bio: "Coach Anthony Sampogna is a United States Marine veteran with a background in high-level competitive sports and years of professional coaching experience. He brings leadership, structure, discipline, and a strong commitment to player development. Coach Anthony is focused on helping Coastal Kraken athletes improve their baseball skills while building confidence, toughness, teamwork, respect, and sportsmanship.",
  },
  {
    name: "Frank Pimenta",
    title: "Coach",
    bio: "Coach Frank Pimenta is a United States Marine veteran with experience playing high-level sports and a strong coaching background. He brings discipline, leadership, competitiveness, and a team-first mindset to Coastal Kraken Baseball. Coach Frank is committed to helping young athletes develop strong fundamentals, confidence, work ethic, respect, and character on and off the field.",
  },
];

function BackgroundCheckedBadge() {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-green-300 bg-green-500/10 border border-green-400/40 rounded-full px-2.5 py-1"
      data-testid="background-checked-badge"
    >
      <ShieldCheck size={11} /> Background Checked
    </span>
  );
}

export default function Coaches() {
  return (
    <div data-testid="coaches-page" className="py-16 sm:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl uppercase font-bold tracking-tight break-words">
          Coaching Staff
        </h1>
        <p className="text-slate-300 mt-5 sm:mt-6 max-w-3xl leading-relaxed text-base sm:text-lg" data-testid="coaches-intro">
          Our coaching staff is committed to developing disciplined players, strong teammates, and leaders of character through competitive baseball, faith, service, and hard work.
        </p>

        {/* Head Coach */}
        <div className="mt-10 sm:mt-12 kr-card rounded-2xl overflow-hidden grid md:grid-cols-[280px_1fr]" data-testid="coach-jackson">
          <div className="bg-gradient-to-br from-[#14213D] to-[#050A15] flex items-center justify-center p-8 sm:p-10 border-b md:border-b-0 md:border-r border-white/10 relative">
            <div className="absolute inset-0 bg-[#00E5FF] opacity-10 blur-3xl" />
            <img src={KRAKEN_OCTOPUS} alt="Coastal Kraken" className="relative w-40 h-40 sm:w-48 sm:h-48 object-contain drop-shadow-[0_0_30px_rgba(0,229,255,0.5)]" />
          </div>
          <div className="p-6 sm:p-8">
            <div className="eyebrow mb-2">{HEAD_COACH.title}</div>
            <h2 className="font-display text-2xl sm:text-3xl uppercase">{HEAD_COACH.name}</h2>
            <div className="mt-3">
              <BackgroundCheckedBadge />
            </div>
            {HEAD_COACH.bio.map((p, i) => (
              <p key={i} className="text-slate-300 mt-4 leading-relaxed text-sm sm:text-base">{p}</p>
            ))}
            <div className="mt-6 flex flex-wrap gap-2">
              {HEAD_COACH.badges.map((t) => (
                <span key={t} className="text-xs uppercase tracking-widest text-[#00E5FF] border border-[#00E5FF]/40 rounded-full px-3 py-1">{t}</span>
              ))}
            </div>

            {/* Certifications */}
            <div className="mt-6 pt-5 border-t border-white/10" data-testid="jackson-certifications">
              <div className="eyebrow mb-3">Certifications</div>
              <div className="flex flex-wrap gap-1.5">
                {CERTIFICATIONS.map((c, i) => (
                  <span
                    key={c.label}
                    className="cert-badge badge-fade-in inline-flex items-center gap-1.5 bg-[#050A15] border border-[#00E5FF]/30 rounded-full pl-1.5 pr-2.5 py-1 text-[11px] text-slate-200 hover:border-[#00E5FF]/60 transition-colors"
                    style={{ animationDelay: `${i * 70}ms` }}
                    data-testid={`cert-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <span className="w-5 h-5 rounded-full bg-[#00E5FF]/10 flex items-center justify-center text-[#00E5FF] shrink-0">
                      <c.icon size={11} />
                    </span>
                    {c.label}
                    <BadgeCheck size={11} className="text-[#00E5FF] shrink-0" />
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Assistant Coaches */}
        <div className="grid sm:grid-cols-2 gap-5 mt-8">
          {COACHES.map((c) => (
            <div key={c.name} className="kr-card rounded-2xl overflow-hidden" data-testid={`coach-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="bg-gradient-to-br from-[#14213D] to-[#050A15] flex items-center justify-center p-8 border-b border-white/10 relative">
                <div className="absolute inset-0 bg-[#00E5FF] opacity-10 blur-2xl" />
                <img src={KRAKEN_OCTOPUS} alt="Coastal Kraken" className="relative w-32 h-32 object-contain drop-shadow-[0_0_24px_rgba(0,229,255,0.4)]" />
              </div>
              <div className="p-5 sm:p-6">
                <div className="eyebrow mb-1">{c.title}</div>
                <h3 className="font-display text-xl sm:text-2xl uppercase break-words">{c.name}</h3>
                <div className="mt-2">
                  <BackgroundCheckedBadge />
                </div>
                <p className="text-slate-300 mt-3 leading-relaxed text-sm">{c.bio}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
